import type { ChatRoom } from '../6.manage/index';

// 応募者評価機能の型定義。
// 2025/6/10のリファクタリングで現行サービスから外れた「審査員が応募者を評価する」機能
// （src/onetime/legacy/recruit-evaluation-2025-06）のスキーマを復元したもの。
//
// 現行の src/type.ts の Applicant には examination/examList* は存在しないため、
// これらは applicants ドキュメントへの追加フィールドとして扱う（既存フィールドには触れない）。
//
// 一覧のベースは applicants への直接クエリではなく chats コレクション（応募者管理
// src/salon/desktop/6.manage/ が実際に company.id で絞り込んでいる、本番で動作実績のある
// コレクション）から作る。applicants は chats と同じドキュメントIDを共有しており
// （6.manage/items/index.tsx の applicationId: chat.id 参照）、評価固有のフィールド
// （examination/examList*）だけを補助的に読み書きする。氏名はchats.user.nameを基本とし、
// applicants.basicInfo.nameがあれば優先する。メモはchats.meta.memo（応募者管理のメモ機能と
// 同じもの）をそのまま表示する。
export type ApplicantRow = {
  id: string; // = chats/{id}のドキュメントID（applicants/{id}と共有）
  recruitId: string;
  name: string;
  time: Date;
  examination: number; // 1〜5=選考中, 6=採用, 7=不採用, 8=辞退
  memo: string; // chats/{id}.meta.memo（応募者管理のメモ欄と同一データ）
  examList1: string[];
  examList2: string[];
  examList3: string[];
  examList4: string[];
  examList5: string[];
};

export type RecruitSelectionConfig = {
  selectionCount: number;
  texts: string[]; // index0 = 審査①のタイトル 〜 index7 = 審査⑧
};

export type ExamRecord = {
  id: string;
  uid: string; // judgeId（useJudgeIdentityが発行する自己申告の識別子。実認証のuidではない）
  name: string;
  evaluation: number; // どの選考段階に対する評価か
  scores: number[]; // index0-7、4=◎ 3=○ 2=△ 1=× 0=未評価
  comments: string[];
  // 総合判断（16.judging/index.tsx の save() が書き込むフィールド。旧来の
  // EvaluationForm.tsx 経由の保存にはこれらのフィールドが無いため、その場合は
  // overallScore=0 / コメント空文字のまま表示側で「未入力」扱いにする）。
  overallScore: number; // 4=◎ 3=○ 2=△ 1=× 0=未入力
  overallComment: string;
  strengthComment: string;
  concernComment: string;
  draft: boolean;
};

export const examinationLabel = (examination: number): string => {
  switch (examination) {
    case 1: return '1次選考';
    case 2: return '2次選考';
    case 3: return '3次選考';
    case 4: return '4次選考';
    case 5: return '5次選考';
    case 6: return '採用';
    case 7: return '不採用';
    // 学生が選考を辞退した。採用・不採用と同じく選考の結論として扱う。
    case 8: return '辞退';
    default: return '未選考';
  }
};

export const scoreLabel = (score: number): string => {
  switch (score) {
    case 4: return '◎';
    case 3: return '○';
    case 2: return '△';
    case 1: return '×';
    default: return '-';
  }
};

// 16.judging/index.tsx の総合判断（OVERALL_OPTIONS/VISIT_OVERALL_OPTIONS）と同じ意味づけ。
// 審査(16.judging)・応募者評価(15.evaluation)どちらの画面でも同じ表記になるよう共有する。
// サロン見学には「次の選考へ進める」という概念がないため、isVisitの場合は
// 「入社してほしいか」という観点の表記にする。
export const overallLabel = (score: number, isVisit?: boolean): string => {
  if (isVisit) {
    switch (score) {
      case 4: return '◎ 強く入社してほしい';
      case 3: return '○ 入社してほしい';
      case 2: return '△ 保留';
      case 1: return '× 見送り';
      default: return '未入力';
    }
  }
  switch (score) {
    case 4: return '◎ 強く次へ進めたい';
    case 3: return '○ 次へ進めたい';
    case 2: return '△ 保留';
    case 1: return '× 見送り';
    default: return '未入力';
  }
};

// サロン見学(chats/{id}/visits/{visitId})の詳細情報。
// visitIdはステータス変更(updateDoc)の際に対象ドキュメントを特定するために保持する。
export type VisitInfo = {
  id: string;
  status: string;
  preferredDates: Date[];
  interestedTopics: string[];
  preferredStoreName: string;
};

// アプリ側 chats/{id}/visits/{visitId}.status の想定値。
// 現時点で確認できているのは "requested" のみ(見学申込直後)。
export function visitStatusLabel(status: string): string {
  switch (status) {
    case 'requested': return '日程調整中';
    case 'confirmed': return '日程確定';
    case 'completed': return '見学済み';
    case 'cancelled': return 'キャンセル';
    default: return '未設定';
  }
}

// 見学ステータス変更セレクトの選択肢。
export const VISIT_STATUS_CHOICES: { value: string; label: string }[] = [
  { value: 'requested', label: '日程調整中' },
  { value: 'confirmed', label: '日程確定' },
  { value: 'completed', label: '見学済み' },
  { value: 'cancelled', label: 'キャンセル' },
];

export const TOPIC_LABELS: Record<string, string> = {
  salary: '給与・待遇',
  workStyle: '勤務体制・休日',
  education: '教育制度',
  debut: 'デビューまで',
  staff: 'スタッフ・人間関係',
  work: '仕事内容',
  other: 'その他',
};

// 一覧・詳細パネルが共有する行データ。「応募(chatType: application)」と
// 「サロン見学(chatType: visit)」の2種別を1つの型にまとめている。
export type EntryKind = 'application' | 'visit' | 'infoSession' | 'coupon';

export type EntryRow = {
  // 応募・見学 = chats/{id}のドキュメントID / 会社説明会・クーポン = `event:${eventEntries のID}`
  id: string;
  kind: EntryKind;
  chatRoom: ChatRoom;
  name: string;
  schoolName: string;
  progressLabel: string;
  applicant: ApplicantRow | null; // kind==='application'のみ
  visit: VisitInfo | null; // kind==='visit'のみ
  event: EventEntry | null; // kind==='infoSession' | 'coupon' のみ
};

// ── 会社説明会・クーポンの申込(eventEntries/{説明会かクーポンのID}_{学生uid}) ──
// 学生アプリの「説明会に参加する」「クーポンを申し込む」で作られる(古いアプリの申込は Cloud Functions が
// メッセージから作る)。どの説明会・クーポンかは targetId と、申込時点の内容 target で分かる。
export type EventKind = 'infoSession' | 'coupon';
export type EventStatus = 'requested' | 'confirmed' | 'done' | 'cancelled';
export type EventEntry = {
  id: string;
  kind: EventKind;
  companyId: string;
  recruitId: string;
  chatId: string;
  uid: string;
  studentName: string;
  targetId: string;
  target: {
    name?: string;          // 説明会
    startAt?: Date | null;
    endAt?: Date | null;
    place?: string;
    treatment?: string;     // クーポン
    priceAfter?: number;
    priceBefore?: number;
  };
  status: EventStatus;
  createdAt: Date;
  updatedAt: Date;
};

export const EVENT_KIND_LABELS: Record<EventKind, string> = { infoSession: '会社説明会', coupon: 'クーポン' };

export const EVENT_STATUS_STEPS: Record<EventKind, { value: EventStatus; label: string }[]> = {
  infoSession: [
    { value: 'requested', label: '申込' },
    { value: 'confirmed', label: '参加確定' },
    { value: 'done', label: '参加済み' },
  ],
  coupon: [
    { value: 'requested', label: '申込' },
    { value: 'confirmed', label: '予約確定' },
    { value: 'done', label: '利用済み' },
  ],
};

export const eventStatusLabel = (kind: EventKind, status: string): string =>
  status === 'cancelled' ? 'キャンセル' : (EVENT_STATUS_STEPS[kind].find((s) => s.value === status)?.label ?? '申込');

export const eventStatusTone = (status: string | undefined | null): StatusTone => {
  switch (status) {
    case 'requested': return 'pending';
    case 'confirmed': return 'progress';
    case 'done': return 'done';
    case 'cancelled': return 'closed';
    default: return 'none';
  }
};

const toDateOrNull = (v: any): Date | null => (v?.toDate ? v.toDate() : v instanceof Date ? v : null);

export const parseEventEntry = (id: string, d: any): EventEntry => ({
  id,
  kind: d?.kind === 'coupon' ? 'coupon' : 'infoSession',
  companyId: d?.companyId ?? '',
  recruitId: d?.recruitId ?? '',
  chatId: d?.chatId ?? '',
  uid: d?.uid ?? '',
  studentName: d?.studentName ?? '',
  targetId: d?.targetId ?? '',
  target: {
    name: d?.target?.name ?? '',
    startAt: toDateOrNull(d?.target?.startAt),
    endAt: toDateOrNull(d?.target?.endAt),
    place: d?.target?.place ?? '',
    treatment: d?.target?.treatment ?? '',
    priceAfter: typeof d?.target?.priceAfter === 'number' ? d.target.priceAfter : Number(d?.target?.priceAfter) || 0,
    priceBefore: typeof d?.target?.priceBefore === 'number' ? d.target.priceBefore : Number(d?.target?.priceBefore) || 0,
  },
  status: (['requested', 'confirmed', 'done', 'cancelled'].includes(d?.status) ? d.status : 'requested') as EventStatus,
  createdAt: toDateOrNull(d?.createdAt) ?? new Date(0),
  updatedAt: toDateOrNull(d?.updatedAt) ?? new Date(0),
});

const fmtEventDate = (d: Date) => `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;

// どの説明会・クーポンかの表示(例: 「Neolive集団説明会 10/12 13:00」「カット ¥3,000」)
export const eventTargetLabel = (e: { kind: EventKind; target: EventEntry['target'] }): string => {
  if (e.kind === 'infoSession') {
    return [e.target.name || '会社説明会', e.target.startAt ? fmtEventDate(e.target.startAt) : ''].filter(Boolean).join(' ');
  }
  const price = e.target.priceAfter ? ` ¥${e.target.priceAfter.toLocaleString('ja-JP')}` : '';
  return `${e.target.treatment || 'クーポン'}${price}`;
};

// 説明会・クーポンの名前だけ(応募者管理の説明会・クーポンの切り替えに使う。日時・金額は出さない)
export const eventTargetTitle = (e: { kind: EventKind; target: EventEntry['target'] }): string =>
  e.kind === 'infoSession' ? (e.target.name || '会社説明会') : (e.target.treatment || 'クーポン');

// 一覧の行・詳細に出す短い印(例: 「説明会 10/12」「クーポン カット」)
export const eventShortLabel = (e: EventEntry): string => {
  if (e.kind === 'infoSession') {
    return `説明会 ${e.target.startAt ? `${e.target.startAt.getMonth() + 1}/${e.target.startAt.getDate()}` : (e.target.name ?? '')}`;
  }
  return `クーポン ${e.target.treatment ?? ''}`;
};

// ステータスの色分け。一覧の「進捗状況」と詳細のステータス欄で同じ色にそろえる。
//   pending  : 薄い黄色(こちらの対応待ち) … 見学=日程調整中 / 応募=未選考
//   progress : 薄い青(進行中)            … 見学=日程確定   / 応募=1〜5次選考
//   done     : 薄い緑(完了)              … 見学=見学済み   / 応募=採用
//   closed   : グレー(終了)              … 見学=キャンセル / 応募=不採用
//   none     : 状態なし(未設定)
export type StatusTone = 'pending' | 'progress' | 'done' | 'closed' | 'none';

export const visitStatusTone = (status: string | undefined | null): StatusTone => {
  switch (status) {
    case 'requested': return 'pending';
    case 'confirmed': return 'progress';
    case 'completed': return 'done';
    case 'cancelled': return 'closed';
    default: return 'none';
  }
};

export const examinationTone = (examination: number | undefined | null): StatusTone => {
  if (examination === 6) return 'done';
  if (examination === 7 || examination === 8) return 'closed';
  if (examination && examination >= 1 && examination <= 5) return 'progress';
  return 'pending';
};

// 学生の写真のURL。応募は応募書類の証明写真(resume/{chatId})、見学はプロフィール写真。
// 詳細の上部とメッセージ欄のアイコンで共通に使う(読み込めないときは呼び出し側で「画像なし」にする)。
export const studentAvatarUrl = (chatRoom: ChatRoom): string =>
  chatRoom.meta.chatType === 'application'
    ? `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/resume%2F${chatRoom.id}?alt=media`
    : `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/profiles%2Fresize_images%2F${chatRoom.user.uid}_500x500?alt=media`;

// ── ステータス変更時のメッセージテンプレート ──
// 応募設定で保存し(recruits/{companyId}_2027.statusMessages)、学生詳細でステータスを変えたときに
// メッセージの入力欄へ入れる。自動では送らず、内容を確認して「送信」を押してもらう
// (不採用など送り直しのきかない連絡があり、日時など人ごとに書き足したい場面もあるため)。
// 本文は保存した文章がそのまま入る(名前などの差し込みはしない)。本文が空のテンプレートは使わない(ステータスが変わるだけ)。
export type StatusMessageKey =
  | 'pass1' | 'pass2' | 'pass3' | 'pass4' | 'hired' | 'rejected' | 'withdrawn'
  | 'visitConfirmed' | 'visitCompleted' | 'visitCancelled'
  | 'sessionConfirmed' | 'sessionDone' | 'sessionCancelled'
  | 'couponConfirmed' | 'couponDone' | 'couponCancelled';

export type StatusMessageTemplate = { key: StatusMessageKey; label: string; description: string; defaultText: string };

export const APPLICATION_MESSAGE_TEMPLATES: StatusMessageTemplate[] = [
  ...[1, 2, 3, 4].map((n) => ({
    key: `pass${n}` as StatusMessageKey,
    label: `${n}次選考通過`,
    description: `${n}次選考から${n + 1}次選考に進めたとき`,
    defaultText: `この度は${n}次選考にお越しいただき、ありがとうございました。\n${n}次選考は通過となりました。\n\n${n + 1}次選考の日程は以下の通りです。\n日時：\n場所：\n\nご都合が合わない場合は、このメッセージにご返信ください。`,
  })),
  {
    key: 'hired',
    label: '採用',
    description: '採用にしたとき',
    defaultText: 'この度は選考にご参加いただき、ありがとうございました。\n厳正なる選考の結果、採用させていただくことになりました。\n\n今後の手続きについては、改めてご連絡いたします。',
  },
  {
    key: 'rejected',
    label: '不採用',
    description: '不採用にしたとき',
    defaultText: 'この度は選考にご参加いただき、ありがとうございました。\n慎重に選考を進めました結果、誠に残念ながら今回はご期待に沿えない結果となりました。\n\n今後のご活躍を心よりお祈り申し上げます。',
  },
  {
    key: 'withdrawn',
    label: '辞退',
    description: '辞退にしたとき',
    defaultText: '選考辞退のご連絡、承知いたしました。\nこの度は選考にご参加いただき、ありがとうございました。\n\n今後のご活躍を心よりお祈り申し上げます。',
  },
];

export const VISIT_MESSAGE_TEMPLATES: StatusMessageTemplate[] = [
  {
    key: 'visitConfirmed',
    label: '日程確定',
    description: '見学の日程を確定したとき',
    defaultText: 'サロン見学のお申し込みありがとうございます。\n見学の日程は以下の通りです。\n日時：\n店舗：\n\n当日お会いできるのを楽しみにしております。',
  },
  {
    key: 'visitCompleted',
    label: '見学済み',
    description: '見学済みにしたとき',
    defaultText: '本日はサロン見学にお越しいただき、ありがとうございました。\n気になることがあれば、いつでもこのメッセージからご連絡ください。',
  },
  {
    key: 'visitCancelled',
    label: 'キャンセル',
    description: '見学をキャンセルにしたとき',
    defaultText: 'サロン見学の件、キャンセルとさせていただきました。\nまた見学をご希望の際は、お気軽にお申し込みください。',
  },
];

export const SESSION_MESSAGE_TEMPLATES: StatusMessageTemplate[] = [
  {
    key: 'sessionConfirmed',
    label: '参加確定',
    description: '会社説明会の参加を確定したとき',
    defaultText: '会社説明会へのお申し込みありがとうございます。\n参加を確定しました。日時・場所は以下の通りです。\n日時：\n場所：\n\n当日お会いできるのを楽しみにしております。',
  },
  {
    key: 'sessionDone',
    label: '参加済み',
    description: '会社説明会を参加済みにしたとき',
    defaultText: '本日は会社説明会にご参加いただき、ありがとうございました。\nサロン見学やご応募もお待ちしております。気になることがあれば、いつでもこのメッセージからご連絡ください。',
  },
  {
    key: 'sessionCancelled',
    label: 'キャンセル',
    description: '会社説明会の申込をキャンセルにしたとき',
    defaultText: '会社説明会の件、キャンセルとさせていただきました。\nまたのご参加をお待ちしております。',
  },
];

export const COUPON_MESSAGE_TEMPLATES: StatusMessageTemplate[] = [
  {
    key: 'couponConfirmed',
    label: '予約確定',
    description: 'クーポンのご予約を確定したとき',
    defaultText: 'クーポンのお申し込みありがとうございます。\nご予約を確定しました。日時・店舗は以下の通りです。\n日時：\n店舗：\n\nご来店をお待ちしております。',
  },
  {
    key: 'couponDone',
    label: '利用済み',
    description: 'クーポンを利用済みにしたとき',
    defaultText: '本日はご来店いただき、ありがとうございました。\nサロン見学や会社説明会もお気軽にお申し込みください。',
  },
  {
    key: 'couponCancelled',
    label: 'キャンセル',
    description: 'クーポンの申込をキャンセルにしたとき',
    defaultText: 'クーポンのお申し込みの件、キャンセルとさせていただきました。\nまたのご利用をお待ちしております。',
  },
];

export const STATUS_MESSAGE_TEMPLATES = [
  ...APPLICATION_MESSAGE_TEMPLATES, ...VISIT_MESSAGE_TEMPLATES, ...SESSION_MESSAGE_TEMPLATES, ...COUPON_MESSAGE_TEMPLATES,
];

// 会社説明会・クーポンの状態を変えたときのテンプレート
export const eventMessageKey = (kind: EventKind, to: string): StatusMessageKey | null => {
  const prefix = kind === 'infoSession' ? 'session' : 'coupon';
  if (to === 'confirmed') return `${prefix}Confirmed` as StatusMessageKey;
  if (to === 'done') return `${prefix}Done` as StatusMessageKey;
  if (to === 'cancelled') return `${prefix}Cancelled` as StatusMessageKey;
  return null;
};

export type StatusMessages = Record<StatusMessageKey, string>;

// 保存されていない(一度も設定を保存していない)会社は例文をそのまま使う
export const resolveStatusMessages = (saved: unknown): StatusMessages => {
  const map = (saved && typeof saved === 'object' ? saved : {}) as Record<string, unknown>;
  return Object.fromEntries(STATUS_MESSAGE_TEMPLATES.map((t) => [
    t.key,
    typeof map[t.key] === 'string' ? map[t.key] : t.defaultText,
  ])) as StatusMessages;
};

// 選考段階を from → to に変えたときのテンプレート。前の段階に戻したときは無し。
export const applicationMessageKey = (from: number, to: number): StatusMessageKey | null => {
  if (to === 6) return 'hired';
  if (to === 7) return 'rejected';
  if (to === 8) return 'withdrawn';
  if (to >= 2 && to <= 5 && to > from) return `pass${to - 1}` as StatusMessageKey;
  return null;
};

export const visitMessageKey = (to: string): StatusMessageKey | null => {
  if (to === 'confirmed') return 'visitConfirmed';
  if (to === 'completed') return 'visitCompleted';
  if (to === 'cancelled') return 'visitCancelled';
  return null;
};


// ── 募集回(第1回募集・第2回募集…) ──
// recruits/{companyId}_2027.recruitPeriods に、回ごとの { deadline: 'YYYY-MM-DD' か '', closedAt: Timestamp か null } を
// 古い順の配列で持つ。名前は並び順から「第n回募集」と付ける。
// 応募がどの回に入るかは保存せず、応募日(chats.meta.createdAt)と各回の終わり(締切日の終わり・締めた日時の早いほう)
// から毎回決める。締切日の後に届いた応募は次の回に入る。最後の回に終わりがあれば、その次の回(まだ保存していない回)も出す。
// 設定が無い会社(今までの会社)は、締切日の無い「第1回募集」1つだけとして扱う。
export type RecruitPeriod = { deadline: string; closedAt: Date | null };
export type PeriodState = 'open' | 'reviewing' | 'closed' | 'upcoming';
export type ResolvedPeriod = RecruitPeriod & {
  index: number;
  name: string;
  start: Date | null; // 前の回の終わり(この時刻より後の応募がこの回に入る)
  end: Date | null;
  state: PeriodState;
  saved: boolean; // recruitPeriods に保存済みか(false はまだ保存していない次の回)
};

export const PERIOD_STATE_LABELS: Record<PeriodState, string> = {
  open: '受付中',
  reviewing: '受付終了・選考中',
  closed: '締め済み',
  upcoming: '受付前',
};

export const periodName = (index: number): string => `第${index + 1}回募集`;

// 締切日はその日の終わり(日本時間 23:59:59)まで受け付ける
export const deadlineEnd = (deadline: string): Date | null =>
  /^\d{4}-\d{2}-\d{2}$/.test(deadline) ? new Date(`${deadline}T23:59:59.999+09:00`) : null;

export const parseRecruitPeriods = (raw: unknown): RecruitPeriod[] => {
  const list = Array.isArray(raw) ? raw : [];
  const periods = list.map((p: any) => ({
    deadline: typeof p?.deadline === 'string' ? p.deadline : '',
    closedAt: p?.closedAt?.toDate ? p.closedAt.toDate() : (p?.closedAt instanceof Date ? p.closedAt : null),
  }));
  return periods.length > 0 ? periods : [{ deadline: '', closedAt: null }];
};

const periodEnd = (p: RecruitPeriod): Date | null => {
  const ends = [deadlineEnd(p.deadline), p.closedAt].filter((d): d is Date => d !== null);
  return ends.length === 0 ? null : new Date(Math.min(...ends.map((d) => d.getTime())));
};

export const resolvePeriods = (periods: RecruitPeriod[], now: Date = new Date()): ResolvedPeriod[] => {
  const list: (RecruitPeriod & { saved: boolean })[] = periods.map((p) => ({ ...p, saved: true }));
  const last = list[list.length - 1];
  if (last && periodEnd(last)) list.push({ deadline: '', closedAt: null, saved: false });
  let start: Date | null = null;
  return list.map((p, index) => {
    const end = periodEnd(p);
    const state: PeriodState = p.closedAt
      ? 'closed'
      : start && now.getTime() <= start.getTime()
        ? 'upcoming'
        : end && now.getTime() > end.getTime() ? 'reviewing' : 'open';
    const resolved = { ...p, index, name: periodName(index), start, end, state };
    start = end;
    return resolved;
  });
};

// 応募日から、その応募が入る回の番号を返す
export const periodIndexFor = (periods: ResolvedPeriod[], appliedAt: Date): number => {
  const t = appliedAt.getTime();
  const found = periods.find((p) => !p.end || t <= p.end.getTime());
  return found ? found.index : periods.length - 1;
};
